import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAuth } from "context/auth.context";
import { useNavigate, useParams } from "react-router-dom";
import Lottie from "react-lottie";

// Custom components
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Icons
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TuneIcon from "@mui/icons-material/Tune";
import DataObjectIcon from "@mui/icons-material/DataObject";
import RateReviewIcon from "@mui/icons-material/RateReview";

// Wizard steps
import BasicInfoStep from "./BasicInfoStep";
import DataSourceStep from "./DataSourceStep";
import ReviewStep from "./ReviewStep";

// Animation
import animationData from "assets/lottie/creating_chatbot.json";

const TemplateCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "popular",
})(({ theme, popular }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderWidth: popular ? "2px" : "1px",
  borderStyle: "solid",
  borderColor: popular ? "#d32f2f" : theme.palette.divider,
  borderRadius: theme.shape.borderRadius * 2,
  position: "relative",
  overflow: "visible",
  cursor: "pointer",
  transition: "transform 0.3s, box-shadow 0.3s, border-color 0.3s, border-width 0.3s",
  "&:hover": {
    transform: "translateY(-10px) scale(1.03)",
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
  },
}));

const templates = [
  {
    title: "E-commerce Assistant",
    description:
      "Increase sales and automate responses to common questions about products, orders, and shipping.",
    icon: <ShoppingCartIcon fontSize="large" sx={{ color: "#fff" }} />,
    iconBg: "#5a49da",
    features: ["Product Recommendations", "Order Tracking", "Shipping FAQs"],
    popular: true,
    knowledgeType: "xml",
    colors: {
      header: "#5a49da",
      button: "#5a49da",
      text: "#222222",
      background: "#F5F5F5",
    },
  },
  {
    title: "Website Assistant",
    description:
      "Engage visitors, provide information about your services, and collect contact details.",
    icon: <LanguageIcon fontSize="large" sx={{ color: "#fff" }} />,
    iconBg: "#5a49da",
    features: ["Services Overview", "Contact Form Integration", "Business Hours"],
    knowledgeType: "contacts",
    colors: {
      header: "#5a49da",
      button: "#5a49da",
      text: "#222222",
      background: "#F5F5F5",
    },
  },
  {
    title: "Portfolio Assistant",
    description:
      "Showcase your work, answer questions about your experience, and make it easy to collaborate.",
    icon: <AccountBoxIcon fontSize="large" sx={{ color: "#fff" }} />,
    iconBg: "#7b1fa2",
    features: ["Project Showcase", "Experience FAQs", "Contact for Collaboration"],
    welcomeMessage: "Hi there! Feel free to ask about my projects or experience.",
    knowledgeType: "contacts",
    colors: {
      header: "#7B1FA2",
      button: "#7B1FA2",
      text: "#222222",
      background: "#F5F5F5",
    },
  },
];

function getSteps() {
  return [
    { label: "Basic Settings", icon: <TuneIcon /> },
    { label: "Data Source", icon: <DataObjectIcon /> },
    { label: "Review & Create", icon: <RateReviewIcon /> },
  ];
}

function getStepContent(step, props) {
  switch (step) {
    case 0:
      return <BasicInfoStep {...props} />;
    case 1:
      return <DataSourceStep {...props} />;
    case 2:
      return <ReviewStep {...props} />;
    default:
      return null;
  }
}

function CreateChatbotWizard({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createdChatbot, setCreatedChatbot] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Existing chatbot ID for editing

  // State for all chatbot properties
  const [name, setName] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("Hello! How can I help you today?");
  const [headerColor, setHeaderColor] = useState("#5a49da");
  const [buttonColor, setButtonColor] = useState("#5a49da");
  const [textColor, setTextColor] = useState("#222222");
  const [backgroundColor, setBackgroundColor] = useState("#f8f9fa");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [dataSources, setDataSources] = useState([]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setName(template.title);
    setWelcomeMessage(
      template.welcomeMessage || `Hello! I am the ${template.title}. How can I help you today?`
    );
    if (template.colors) {
      setHeaderColor(template.colors.header);
      setButtonColor(template.colors.button);
      setTextColor(template.colors.text);
      setBackgroundColor(template.colors.background);
    }
    setActiveStep(activeStep + 1);
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarUrl(URL.createObjectURL(file));
    }
  };

  const handleAvatarUrlSelect = (url) => {
    setAvatarUrl(url);
    setAvatarFile(null); // Clear any uploaded file to prioritize URL
  };

  const handleSave = async () => {
    // This function will now handle the final save logic
    setError("");
    setLoading(true);

    try {
      let finalChatbotId = id;

      // 1. Create or Update chatbot basic info
      const chatbotPayload = {
        name,
        user_id: user?.id,
        config: {
          welcomeMessage,
          headerColor,
          buttonColor,
          textColor,
          backgroundColor,
          avatar: avatarUrl && !avatarUrl.startsWith("blob:") ? avatarUrl : undefined,
          suggestedQuestions: [],
          intents: [],
        },
      };

      const endpoint = id ? `/api/chatbots/${id}` : "/api/chatbots";
      const method = id ? "PUT" : "POST";

      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(chatbotPayload),
      });

      const chatbotData = await res.json();
      if (!res.ok) {
        throw new Error(chatbotData.message || "Error saving the chatbot");
      }
      finalChatbotId = chatbotData.id;

      // 2. Handle Avatar Upload if a new file is present
      if (avatarFile) {
        const formData = new FormData();
        formData.append("avatar", avatarFile);
        const avatarRes = await fetch(`/api/chatbots/${finalChatbotId}/avatar`, {
          method: "POST",
          body: formData,
        });
        if (!avatarRes.ok) console.error("Avatar upload failed but continuing.");
      }

      // 3. Handle Data Source Ingestion (if applicable)
      if (dataSources.length > 0 && dataSources[0].webUrl) {
        const url = dataSources[0].webUrl.endsWith("/")
          ? dataSources[0].webUrl.slice(0, -1)
          : dataSources[0].webUrl;
        const fullUrl = url + (dataSources[0].webPath || "");
        const ingestBody = {
          rss_url: fullUrl,
          chatbot_id: chatbotData.token,
          knowledge_type: dataSources[0].knowledgeType,
        };
        // This can be a fire-and-forget or you can await it
        fetch("/v1/rss-ingest", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ingestBody),
        });
      }

      handleClose(true); // Close dialog on success
      navigate("/chatbot-configuration", { state: { token: chatbotData.token } }); // Navigate to chatbot config
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);

  const resetState = () => {
    setActiveStep(0);
    setSelectedTemplate(null);
    setName("");
    setWelcomeMessage("Hello! How can I help you today?");
    setHeaderColor("#5a49da");
    setButtonColor("#5a49da");
    setTextColor("#222222");
    setBackgroundColor("#f8f9fa");
    setAvatarFile(null);
    setAvatarUrl(null);
    setDataSources([]);
    setError("");
    setLoading(false);
  };

  const handleClose = (isSuccess = false) => {
    if (!isSuccess) {
      resetState();
    }
    onClose();
    // Delay reset to allow closing animation
    setTimeout(resetState, 300);
  };

  const steps = getSteps();
  const isLastStep = activeStep === steps.length;

  const allProps = {
    name,
    setName,
    welcomeMessage,
    setWelcomeMessage,
    headerColor,
    setHeaderColor,
    buttonColor,
    setButtonColor,
    textColor,
    setTextColor,
    backgroundColor,
    setBackgroundColor,
    avatarUrl,
    handleAvatarUpload,
    handleAvatarUrlSelect,
    dataSources,
    setDataSources,
  };

  const defaultLottieOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <Dialog open={open} onClose={() => handleClose(false)} fullWidth maxWidth="lg">
      <DialogTitle sx={{ textAlign: "center", pt: 4, pb: 1, position: "relative" }}>
        {activeStep > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1, mt: -2 }}>
            <Lottie options={defaultLottieOptions} height={100} width={220} />
          </Box>
        )}
        <MDTypography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
          {activeStep === 0 ? "Choose a Template" : "Create Your Chatbot"}
        </MDTypography>
        <MDTypography
          variant="body1"
          color="secondary"
          sx={{ minHeight: activeStep === 0 ? "1.43em" : 0 }}
        >
          {activeStep === 0
            ? "Get started quickly with a pre-configured template that fits your needs."
            : null}
        </MDTypography>
        <IconButton
          aria-label="close"
          onClick={() => handleClose(false)}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
        {activeStep === 0 ? (
          <>
            <Grid container spacing={4} sx={{ mt: 3 }}>
              {templates.map((template) => (
                <Grid item xs={12} md={4} key={template.title}>
                  <TemplateCard
                    popular={template.popular}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    {template.popular && (
                      <Chip
                        label="Popular"
                        color="warning"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 16,
                          transform: "translateY(-50%)",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                    <CardContent
                      sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column" }}
                    >
                      <Avatar sx={{ bgcolor: template.iconBg, width: 60, height: 60, mb: 2 }}>
                        {template.icon}
                      </Avatar>
                      <MDTypography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                        {template.title}
                      </MDTypography>
                      <MDTypography color="secondary" sx={{ mb: 3, flexGrow: 1 }}>
                        {template.description}
                      </MDTypography>
                      <List dense sx={{ mb: 3 }}>
                        {template.features.map((feature) => (
                          <ListItem key={feature} disableGutters sx={{ p: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <MDTypography variant="body2" color="text">
                                  {feature}
                                </MDTypography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<BoltIcon />}
                        sx={(theme) => ({
                          mt: "auto",
                          textTransform: "none",
                          fontWeight: "bold",
                          py: 1.5,
                          borderRadius: "12px",
                          color: "#fff",
                          pointerEvents: "none",
                          background: template.popular
                            ? `linear-gradient(to right, ${theme.palette.custom.main}, ${theme.palette.custom.focus})`
                            : "rgb(17, 24, 39)",
                          "&:hover": {
                            background: template.popular
                              ? `linear-gradient(to right, ${theme.palette.custom.focus}, ${theme.palette.custom.main})`
                              : "rgb(55, 65, 81)",
                          },
                        })}
                      >
                        Use This Template
                      </Button>
                    </CardContent>
                  </TemplateCard>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button variant="text" sx={{ textTransform: "none", fontWeight: "bold" }}>
                Start from Scratch Instead
              </Button>
            </Box>
          </>
        ) : (
          // Stepper view
          <Box sx={{ width: "100%", mt: 1 }}>
            <Stepper activeStep={activeStep - 1} alternativeLabel>
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {React.cloneElement(step.icon, {
                        fontSize: "small",
                        sx: { mr: 0.5 },
                      })}
                      {step.label}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ mt: 3, mb: 2 }}>{getStepContent(activeStep - 1, allProps)}</Box>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                color="inherit"
                disabled={activeStep === 1}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              <MDButton
                onClick={isLastStep ? handleSave : handleNext}
                disabled={loading}
                variant="gradient"
                color="info"
              >
                {loading ? "Processing..." : isLastStep ? "Save & Finish" : "Next"}
              </MDButton>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

CreateChatbotWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateChatbotWizard;
